import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, X, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { useAuth } from "@/hooks/auth";

export interface ClientsFilters {
  name?: string;
  email?: string;
  document?: string;
  client_origin_id?: number;
  client_category_id?: number;
  has_purchase_voucher?: boolean;
  has_discount?: boolean;
  is_new?: boolean;
  order_by?: 'name' | 'total_balance' | 'created_at';
  order_direction?: 'asc' | 'desc';
  per_page?: number;
}

interface ClientsFiltersComponentProps {
  filters: ClientsFilters;
  onFiltersChange: (filters: ClientsFilters) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  origins?: Array<{ id: number; name: string }>;
  categories?: Array<{ id: number; name: string }>;
}

export const ClientsFiltersComponent = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  origins = [],
  categories = [],
}: ClientsFiltersComponentProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const user = useAuth();
  const isSeller = !!user?.user?.seller?.id;

  const updateFilter = (key: keyof ClientsFilters, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value !== undefined && value !== null && value !== 'all' ? value : undefined,
    });
  };

  const hasActiveFilters = Object.entries(filters).some(
    ([key, value]) => value && key !== 'order_by' && key !== 'order_direction' && key !== 'per_page'
  );

  return (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">Filtros</CardTitle>
          <div className="flex gap-2">
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={onClearFilters}
                className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 bg-red-50"
              >
                <X className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className={cn(
                "h-8",
                isExpanded && "bg-primary/10 dark:bg-primary/20"
              )}
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-1"  />
                  Ocultar
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Expandir
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="space-y-4 pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Campo Nome/Telefone */}
            <div className="space-y-2">
              <Label htmlFor="filter-name">Nome ou Telefone</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="filter-name"
                  placeholder="Buscar por nome ou telefone"
                  value={filters.name || ''}
                  onChange={(e) => updateFilter('name', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && onApplyFilters()}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Campo Email */}
            <div className="space-y-2">
              <Label htmlFor="filter-email">Email</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="filter-email"
                  type="email"
                  placeholder="Buscar por email"
                  value={filters.email || ''}
                  onChange={(e) => updateFilter('email', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && onApplyFilters()}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Campo Documento */}
            <div className="space-y-2">
              <Label htmlFor="filter-document">CPF/CNPJ</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="filter-document"
                  placeholder="Buscar por documento"
                  value={filters.document || ''}
                  onChange={(e) => updateFilter('document', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && onApplyFilters()}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Select Origem */}
            {origins.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="filter-origin">Origem</Label>
                <Select
                  value={filters.client_origin_id?.toString() || 'all'}
                  onValueChange={(value) => 
                    updateFilter('client_origin_id', value !== 'all' ? parseInt(value) : undefined)
                  }
                >
                  <SelectTrigger id="filter-origin">
                    <SelectValue placeholder="Todas as origens" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {origins.map((origin) => (
                      <SelectItem key={origin.id} value={origin.id.toString()}>
                        {origin.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Select Categoria */}
            {categories.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="filter-category">Categoria</Label>
                <Select
                  value={filters.client_category_id?.toString() || 'all'}
                  onValueChange={(value) =>
                    updateFilter('client_category_id', value !== 'all' ? parseInt(value) : undefined)
                  }
                >
                  <SelectTrigger id="filter-category">
                    <SelectValue placeholder="Todas as categorias" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id.toString()}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Filtro Vale Compra */}
            <div className="space-y-2">
              <Label htmlFor="filter-purchase-voucher">Aceita Vale Compra</Label>
              <Select
                value={filters.has_purchase_voucher === undefined ? 'all' : filters.has_purchase_voucher ? 'yes' : 'no'}
                onValueChange={(value) =>
                  updateFilter('has_purchase_voucher', value === 'all' ? undefined : value === 'yes')
                }
              >
                <SelectTrigger id="filter-purchase-voucher">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="yes">Sim</SelectItem>
                  <SelectItem value="no">Não</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filtro Antigo/Novo */}
            <div className="space-y-2">
              <Label htmlFor="filter-is-new">Antigo/Novo</Label>
              <Select
                value={filters.is_new === undefined ? 'all' : filters.is_new ? 'new' : 'old'}
                onValueChange={(value) =>
                  updateFilter('is_new', value === 'all' ? undefined : value === 'new')
                }
              >
                <SelectTrigger id="filter-is-new">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="new">Novo</SelectItem>
                  <SelectItem value="old">Antigo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filtro Contém Desconto - Oculto para vendedores */}
            {!isSeller && (
              <div className="space-y-2">
                <Label htmlFor="filter-has-discount">Contém Desconto</Label>
                <div className="flex items-center h-10 px-3 border rounded-md bg-background">
                  <Switch
                    id="filter-has-discount"
                    checked={filters.has_discount === true}
                    onCheckedChange={(checked) =>
                      updateFilter('has_discount', checked ? true : undefined)
                    }
                  />
                  <span className="ml-2 text-sm text-muted-foreground">
                    {filters.has_discount ? 'Sim' : 'Não'}
                  </span>
                </div>
              </div>
            )}

            {/* Itens por página */}
            <div className="space-y-2">
              <Label htmlFor="filter-per-page">Itens por página</Label>
              <Select
                value={filters.per_page?.toString() || '15'}
                onValueChange={(value) => 
                  updateFilter('per_page', parseInt(value))
                }
              >
                <SelectTrigger id="filter-per-page">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Botão aplicar */}
          <div className="flex justify-end">
            <Button 
              onClick={onApplyFilters}
              className={cn("px-6", colors.bg_primary)}
            >
              <Search className="w-4 h-4 mr-2" />
              Aplicar Filtros
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};